/**
 * Shared markdown + maths renderer for every `v-html` surface.
 *
 * Before this module existed, seven components each called `marked.parse()` on
 * the global singleton with no shared config. Maths therefore rendered as
 * literal LaTeX — a maths question arrived as `$$p(x) = 3x^4 - 7x^3$$` and the
 * student saw exactly that, delimiters and carets included. Consolidating the
 * call sites here is what makes the maths fix — and the sanitiser below — a
 * single edit rather than seven.
 *
 * Three deliberate choices:
 *
 * 1. **KaTeX is loaded on demand, not bundled into the entry chunk.** KaTeX is
 *    not tree-shakeable (one monolithic module, no `sideEffects` flag), so a
 *    static import adds ~76 KB gzip to the main chunk for every visitor —
 *    including the Today and Subject surfaces, whose only markdown is the
 *    deterministic subject narrative and contains no maths at all. Instead the
 *    first text containing a `$` triggers the import, and `mathReady` re-runs
 *    the computeds that called us. `preloadMath()` lets the session overlay
 *    start the fetch on open, so by the time a question returns from the API
 *    (behind a multi-second loader) KaTeX is already there and no student ever
 *    sees the pre-load fallback.
 *
 * 2. **A private `Marked` instance, not the global singleton.** `marked.use()`
 *    mutates global state; scoping the maths extension to our own instance
 *    keeps it from leaking into any unrelated `marked` import.
 *
 * 3. **Every returned string is DOMPurify-sanitised.** Everything here is
 *    rendered with `v-html`, and `marked` v18 dropped its own `sanitize`
 *    option in favour of exactly this. Not all of the input is LLM-authored:
 *    tutor chat renders the student's own `role: 'user'` turns through this
 *    function, and the account model gives users names that other users see.
 *    Sanitising once here is what keeps that a single edit rather than one per
 *    `v-html` site.
 */
import DOMPurify, { type Config } from "dompurify";
import { Marked } from "marked";
import { ref } from "vue";

/**
 * Cheap pre-filter deciding whether a string is worth loading KaTeX for. A
 * lone `$` cannot delimit anything, so two is the floor. This only gates the
 * *import* — the extension's own regexes decide what is actually maths.
 */
const MATH_HINT = /\$[^$]*\$/;

/**
 * `breaks: true` is deliberate and load-bearing. Every string routed through
 * here is LLM-authored, line-oriented text — mark schemes are generated as one
 * `Mn:` point per line (the generator prompt mandates it), and tutor replies
 * use single newlines as real breaks. Standard markdown collapses a single
 * newline to a space, which rendered a four-point mark scheme as one run-on
 * paragraph. These are not hand-written markdown documents whose author knew
 * `\n` was insignificant, so honouring the newline is the correct reading.
 */
const instance = new Marked({ breaks: true });

/**
 * Flipped once the maths extension is registered. Read on every render so the
 * Vue computeds that call `renderMarkdown` take a reactive dependency on it and
 * re-evaluate when KaTeX lands.
 */
const mathReady = ref(false);

let inFlight: Promise<void> | null = null;

/**
 * Load KaTeX and register the maths extension. Idempotent and safe to call from
 * a render path — the promise is cached, so N components racing on the same
 * question produce one import.
 *
 * Failure is deliberately swallowed to a warning: if the chunk cannot be
 * fetched (offline, cache miss mid-session) the student should still get their
 * question with literal `$` delimiters rather than an exception thrown inside a
 * computed, which would blank the whole panel.
 */
export function preloadMath(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = Promise.all([
    import("katex"),
    import("katex/dist/katex.min.css"),
    // Rendered maths is a MathML tree plus a visual HTML tree, so a plain
    // selection copies BOTH and a student copying a question gets the
    // expression twice, flattened: "𝑦=2𝑥3−9𝑥2+12𝑥−4y=2x 3 −9x 2 +12x−4".
    // That matters because the clipboard is a real path back into the product
    // — students paste questions into tutor chat. This official KaTeX contrib
    // hooks one document-level `copy` listener and substitutes the original
    // TeX from the <annotation>, so a copy round-trips as `$$…$$`. It also
    // keeps the MathML in the DOM, so screen-reader semantics are untouched —
    // which is why this is the fix rather than `output: 'html'`.
    import("katex/contrib/copy-tex"),
    import("marked-katex-extension"),
  ])
    .then(([, , , ext]) => {
      instance.use(
        ext.default({
          // A malformed expression renders in `errorColor` instead of throwing.
          // Non-negotiable: these strings come from an LLM, and one bad
          // fragment must not take out the surrounding question.
          throwOnError: false,
          // KaTeX's default, set explicitly because it is load-bearing:
          // `trust: false` refuses \href, \url, \includegraphics and the
          // \html* attribute commands, which is what keeps rendering
          // model-authored (and, in tutor chat, student-authored) TeX safe.
          trust: false,
          // We cannot act on a console warning about someone else's TeX, and a
          // noisy console hides real errors.
          strict: "ignore",
          // Drops the requirement that a closing `$` be followed by whitespace
          // or punctuation. With the default rule `$x$-axis` and `$f(x)$)` —
          // both ordinary in maths prose — would leak as literal text, i.e.
          // the exact bug this module fixes, just narrower. The cost is that a
          // literal dollar amount must be escaped as `\$`; the generator
          // prompt mandates that.
          nonStandard: true,
        }),
      );
      mathReady.value = true;
    })
    .catch((err: unknown) => {
      console.warn(
        "[markdown] maths renderer unavailable; falling back to plain markdown",
        err,
      );
    });
  return inFlight;
}

/**
 * DOMPurify config. This is DOMPurify's default policy plus two MathML
 * elements and minus the form controls — deliberately not an `ALLOWED_TAGS`
 * allowlist of our own, because the default already covers everything `marked`
 * emits (tables, lists, code, links, images) and everything KaTeX emits, and a
 * hand-rolled list would silently drop whichever of those we forgot.
 *
 * `ADD_TAGS`: KaTeX's default `htmlAndMathml` output wraps the MathML tree in
 * `<semantics>` and closes it with `<annotation encoding="application/x-tex">`
 * holding the original TeX. DOMPurify classifies both as *disallowed* MathML
 * (`mathMlDisallowed`), so without this every equation loses its annotation —
 * which is what `katex/contrib/copy-tex` reads to make a copied question
 * round-trip as `$$…$$`, and what screen readers fall back to. The rest of
 * KaTeX's output needs no widening: the MathML elements, the SVG paths for
 * stretchy delimiters, `class`, `aria-hidden`, `encoding`, `xmlns` and the
 * inline `style` heights are all in DOMPurify's defaults already.
 *
 * `annotation-xml` is deliberately NOT added. It is the one MathML element the
 * HTML parser treats as an integration point (`encoding="text/html"` switches
 * its children back to HTML parsing), which is a classic mXSS vector, and
 * KaTeX never emits it.
 *
 * `FORBID_TAGS`: a narrowing, not a widening. `marked` cannot emit form
 * controls from markdown and KaTeX has no use for them, so the only way one
 * reaches this function is as raw HTML in someone's typed text — where it is a
 * credential-harvesting form pointed at an attacker's origin, rendered inside
 * our chrome. Nothing legitimate is lost by dropping them.
 *
 * Not covered, on purpose: the `style` attribute survives, because KaTeX
 * positions every glyph with inline `height`/`vertical-align`. CSS is not a
 * script-execution vector in any browser we support (`expression()` died with
 * IE), so this costs layout abuse inside an already-untrusted block, not code
 * execution — and stripping it would strip every equation's layout with it.
 */
const PURIFY: Config = {
  ADD_TAGS: ["semantics", "annotation"],
  FORBID_TAGS: ["form", "input", "textarea", "select", "option", "button"],
};

/**
 * Render markdown (with LaTeX maths) to a sanitised HTML string.
 *
 * Synchronous by contract so it can be used directly inside a `computed`.
 */
export function renderMarkdown(src: string | null | undefined): string {
  if (!src) return "";
  // Read unconditionally: this is the reactive dependency that re-renders the
  // caller once KaTeX has loaded. Reading it only inside the branch below
  // would leave already-rendered maths frozen in its fallback form.
  const ready = mathReady.value;
  if (!ready && MATH_HINT.test(src)) void preloadMath();
  return DOMPurify.sanitize(
    instance.parse(src, { async: false }) as string,
    PURIFY,
  );
}
